import React, { useState, useCallback } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { SearchWidget } from './components/SearchWidget';
import { I18nProvider } from './i18n/I18nContext';
import { AdminLogin } from './admin/AdminLogin';
import { AppLayout } from './admin/AppLayout';
import AdminSearchPreview from './admin/pages/AdminSearchPreview';
import AdminAnalytics from './admin/pages/AdminAnalytics';
import AdminFeedsPage from './admin/pages/AdminFeedsPage';
import AdminLayoutPage from './admin/pages/AdminLayoutPage';
import AdminSearchConfig from './admin/pages/AdminSearchConfig';
import AdminAISearch from './admin/pages/AdminAISearch';
import AdminMarketingPage from './admin/pages/AdminMarketingPage';
import AdminCategoriesFiltersPage from './admin/pages/AdminCategoriesFiltersPage';
import AdminSystem from './admin/pages/AdminSystem';

/* ------------------------------------------------------------------ */
/*  Admin route wrapper with auth                                      */
/* ------------------------------------------------------------------ */

const AdminRoute: React.FC = () => {
  const navigate = useNavigate();
  const [secret, setSecret] = useState<string | null>(() =>
    sessionStorage.getItem('botilia_admin_secret'),
  );

  const handleLogin = useCallback(
    (s: string) => {
      setSecret(s);
      navigate('/admin', { replace: true });
    },
    [navigate],
  );

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem('botilia_admin_secret');
    setSecret(null);
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  if (!secret) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <AppLayout secret={secret} onLogout={handleLogout} />;
};

/* ------------------------------------------------------------------ */
/*  Full-page search demo                                              */
/* ------------------------------------------------------------------ */

const SearchDemo: React.FC = () => {
  const [lang, setLang] = useState<'el' | 'en'>('el');

  return (
    <I18nProvider lang={lang}>
    <div className="min-h-screen bg-gradient-to-b from-cream to-white">
      {/* Hero header */}
      <header className="pt-12 pb-8 px-4 text-center">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-wine-dark mb-2">
          Botilia.gr
        </h1>
        <p className="text-gray-500 text-sm sm:text-base max-w-md mx-auto">
          AI-powered search - Βρείτε κρασιά, άρθρα και περισσότερα
        </p>
        <div className="mt-3 inline-flex items-center gap-1 bg-white rounded-full border border-gray-200 p-0.5">
          <button
            onClick={() => setLang('el')}
            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
              lang === 'el' ? 'bg-wine text-white' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Ελληνικά
          </button>
          <button
            onClick={() => setLang('en')}
            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
              lang === 'en' ? 'bg-wine text-white' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            English
          </button>
        </div>
      </header>

      {/* Search widget */}
      <div className="max-w-2xl mx-auto px-4 pb-16">
        <SearchWidget lang={lang} />
      </div>

      {/* Footer */}
      <footer className="fixed bottom-0 inset-x-0 py-3 text-center bg-white/80 backdrop-blur-sm border-t border-gray-100">
        <p className="text-xs text-gray-400">
          Botilia.gr AI Search &middot; Powered by{' '}
          <span className="text-wine font-medium">@botilia/search</span>
        </p>
      </footer>
    </div>
    </I18nProvider>
  );
};

/* ------------------------------------------------------------------ */
/*  App root                                                           */
/* ------------------------------------------------------------------ */

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public search demo */}
        <Route path="/" element={<SearchDemo />} />

        {/* Admin login shortcut */}
        <Route path="/admin/login" element={<AdminRouteLogin />} />

        {/* Admin panel - nested routes via AppLayout (Outlet) */}
        <Route path="/admin" element={<AdminRoute />}>
          <Route index element={<AdminSearchPreview />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="feeds" element={<AdminFeedsPage />} />
          <Route path="layout" element={<AdminLayoutPage />} />
          <Route path="search-config" element={<AdminSearchConfig />} />
          <Route path="ai-search" element={<AdminAISearch />} />
          <Route path="categories-filters" element={<AdminCategoriesFiltersPage />} />
          <Route path="marketing" element={<AdminMarketingPage />} />
          <Route path="system" element={<AdminSystem />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

/** Redirect from /admin/login -> admin route (handled by AdminRoute) */
const AdminRouteLogin: React.FC = () => {
  const navigate = useNavigate();
  const handleLogin = useCallback(
    (secret: string) => {
      sessionStorage.setItem('botilia_admin_secret', secret);
      navigate('/admin', { replace: true });
    },
    [navigate],
  );
  return <AdminLogin onLogin={handleLogin} />;
};

export default App;
